import {
  Box,
  Button,
  Dot,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ErrorBoundary,
  Text,
} from "@prismicio/editor-ui"
import { type ReactNode, Suspense } from "react"

import { getBaseRepositoryUrl, getRepositoryName } from "../../app/routing"
import {
  type Environment,
  useEnvironments,
} from "../../app/services/environments"
import { useHasSettingsPermission } from "../DocumentList.utils"

export function EnvironmentsDropdownMenu() {
  return (
    <ErrorBoundary
      renderError={() => (
        <Text color="grey11" variant="small">
          Error getting environments
        </Text>
      )}
    >
      <Suspense
        fallback={
          <EnvironmentItem
            notificationColor="grey"
            text="..."
            textColor="grey8"
          />
        }
      >
        <Environments />
      </Suspense>
    </ErrorBoundary>
  )
}

function Environments() {
  const { environments, otherEnvironments, currentEnvironment } =
    useEnvironments()
  const hasSettingsPermission = useHasSettingsPermission()

  if (!currentEnvironment) return null

  const { name: currentEnvironmentName, color: currentEnvironmentColor } =
    environmentKindToUIData[currentEnvironment?.kind]

  if (environments.length === 1) {
    return (
      <EnvironmentItem
        notificationColor={currentEnvironmentColor}
        text={currentEnvironmentName}
        textColor="grey11"
      />
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Box gap={8} alignItems="center">
          <Dot color={currentEnvironmentColor} />

          <Button size="small" endIcon="arrowDropDown" invisible>
            <Text color="grey11" variant="small">
              {currentEnvironmentName}
            </Text>
          </Button>
        </Box>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" maxHeight={256} minWidth={256}>
        {otherEnvironments.map((environment) => {
          const { name, color } = environmentKindToUIData[environment.kind]
          const linkToEnvironment = `${getBaseRepositoryUrl(
            environment.domain,
          )}/builder`

          return (
            <EnvironmentLink href={linkToEnvironment} key={environment.domain}>
              <DropdownMenuItem
                key={environment.domain}
                disabled={environment.name === getRepositoryName()}
                onSelect={(event) => event.preventDefault()}
                textValue={environment.name}
                description={
                  <EnvironmentItem
                    notificationColor={color}
                    text={name}
                    textColor="grey11"
                  />
                }
              >
                {environment.name}
              </DropdownMenuItem>
            </EnvironmentLink>
          )
        })}

        {hasSettingsPermission && (
          <a href="/settings/environments" style={{ textDecoration: "none" }}>
            <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
              <Text color="grey11" variant="small">
                Manage Environments
              </Text>
            </DropdownMenuItem>
          </a>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface EnvironmentItemProps {
  notificationColor: "grey" | "green" | "purple" | "amber"
  textColor: "grey8" | "grey11"
  text: string
}

function EnvironmentItem(props: EnvironmentItemProps) {
  const { notificationColor, textColor, text } = props

  return (
    <Box gap={8} alignItems="center">
      <Dot color={notificationColor} />
      <Text color={textColor} variant="small">
        {text}
      </Text>
    </Box>
  )
}

interface EnvironmentLinkProps {
  href: string
  children: ReactNode
}

function EnvironmentLink(props: EnvironmentLinkProps) {
  const { href, children } = props

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{ textDecoration: "none" }}
    >
      {children}
    </a>
  )
}

const environmentKindToUIData: Record<
  Environment["kind"],
  {
    name: string
    color: EnvironmentItemProps["notificationColor"]
  }
> = {
  prod: { name: "Production", color: "green" },
  stage: { name: "Stage", color: "purple" },
  dev: { name: "Development", color: "amber" },
}
